import React, { useMemo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { Typography } from 'antd';
import {
  ProjectConstructorNavigationControls,
  ProjectsAddSheetForm,
  ProjectsConstructorLayout,
  SheetCardsList,
  useNavigateConstructorTabs,
  validateConstructor,
  validateSheetsTab,
} from '@/views';
import { ProjectDto } from '@/core';
import { ErrorCard, notify } from '@/components';
import { useFinishConstructorMutation } from '@/redux';
import classes from './ProjectsSheetsPage.module.scss';

export const ProjectsSheetsPage = () => {
  const { t } = useTranslation(undefined, { keyPrefix: 'projects.constructor' });
  const navigate = useNavigate();
  const projectData = useOutletContext<ProjectDto>();
  const { navigateToPreviousTab } = useNavigateConstructorTabs(projectData);
  const isSheetsTabValid = validateSheetsTab(projectData);
  const validator = useMemo(() => validateConstructor(projectData), [projectData]);
  const isProjectValid = Object.values(validator).every((x) => x.isValid);
  const [finishConstructor] = useFinishConstructorMutation();
  const [isErrorNotification, setIsErrorNotification] = useState(false);

  useEffect(() => {
    projectData.sheets.length && setIsErrorNotification(false);
  }, [projectData]);

  const navigateToProjectPage = async () => {
    if (!projectData.sheets.length) {
      setIsErrorNotification(true);
      return;
    }
    if (!isProjectValid) {
      notify.error(t('invalidProject'));
      return;
    }
    await finishConstructor({ id: projectData.id }).unwrap();
    navigate(`/projects/project/${projectData.id}`);
  };

  return (
    <ProjectsConstructorLayout
      projectData={projectData}
      content={
        <>
          <Typography.Title level={3}>
            {projectData?.name}: {t('addSheets.title')}
          </Typography.Title>
          <Typography.Text type="secondary" className={classes.pageDescription}>
            {t('addSheets.description')}
          </Typography.Text>
          <ProjectsAddSheetForm projectData={projectData} />
          {!isSheetsTabValid ? <ErrorCard text={t('addSheets.invalidCard')} /> : null}
          {isErrorNotification ? <ErrorCard text={t('addSheets.invalidPage')} /> : null}
          {projectData.sheets.length ? <SheetCardsList projectData={projectData} /> : null}
        </>
      }
      navigationControls={
        <ProjectConstructorNavigationControls
          navigateToPreviousTab={navigateToPreviousTab}
          completeConstructor={navigateToProjectPage}
        />
      }
    />
  );
};
